import _ from "lodash";
import React from "react";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classes from "./ImageDropzoneV2.scss";
import PropTypes from "prop-types";
import DropzoneComponent from "UIComponents/DropzoneComponent";
import I18nHOC from "UIComponents/I18nHOC";
import MediaModal from "UIComponents/MediaModal";
import { getThumbnailForVideo, getRethumbUrl } from "Utils";
import classNames from "classnames";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import ResourceBank from "AppComponents/ResourceBank";
import ACLStore from "lib/aclStore";
import { uploadFileV2 } from "modules/Services";
import { Upload } from "@toddle-edu/ds-web";
import { ImageSquareOutlined, VideosOutlined } from "@toddle-edu/ds-icons";

const getRethumb = (localUrl, imageUrl) => {
  let rethumpUrl = "";
  if (localUrl) {
    rethumpUrl = localUrl;
  } else {
    rethumpUrl = imageUrl
      ? getRethumbUrl({
          width: 700,
          height: 700,
          imageUrl: imageUrl,
          fitIn: true,
        })
      : "";
  }
  return rethumpUrl;
};

class ImageDropZoneV2 extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      isDeleted: false,
      uploadObj: {},
      uploadingImage: false,
      showMediaModal: false,
      showResourceBank: false,
      fileCount: 0,
      fileType: _.get(props, "fileType", "IMAGE"),
      videoPreviewImage: _.get(props, "image", ""),
      file: "",
      croppedFile: undefined,
    };

    this.dropzoneRef = React.createRef();
  }

  onCancel = () => {
    this.setState({ file: "" });
  };

  //As we want to support both "VIDEO" and "IMAGE" we have to pass the file type in the updated value.
  onUpdateValue = value => {
    const { isVideoSupported } = this.props;
    const { fileType } = this.state;

    if (isVideoSupported) {
      this.updateValue({ value, fileType: fileType });
    } else {
      this.updateValue(value);
    }
  };

  waitFor = conditionFunction => {
    const helper = resolve => {
      if (conditionFunction()) resolve();
      else setTimeout(() => helper(resolve), 500);
    };

    return new Promise(helper);
  };

  onDeleteClick = () => {
    this.onUpdateValue("");
    if (this.props.showCroppingModal && !this.props.isVideoSupported) {
      this.setState({
        croppedFile: undefined,
      });
    }
  };

  onContentUploaded = params => {
    if (params.length > 0) {
      this.onUpdateValue(params[0].url);
    } else {
      this.setState({
        croppedFile: undefined,
        ...(this.props.showCroppingModal && !this.props.isVideoSupported
          ? {}
          : { file: "" }),
      });
    }
    this.onUploaded();
  };

  onSingleContentUploaded = async params => {
    this.onUpdateValue(params.url);
    this.onUploaded();
  };

  onUploaded = () => {
    this.setState({ uploadingImage: false, uploadObj: {} });
    this.onBlur();
  };

  onDropContent = params => {
    const { onDropContent } = this.props;
    const fileType = params.type;

    this.setState({ uploadObj: params, fileType });
    if (fileType === "VIDEO") {
      this.getPreviewImageFromVideo({ url: _.get(params, "url", "") });
    }

    onDropContent && onDropContent();
  };

  getPreviewImageFromVideo = async ({ url }) => {
    const imageUrl = await getThumbnailForVideo({ url });
    this.setState({ videoPreviewImage: imageUrl });
    return imageUrl;
  };

  updateFilesCount = count => {
    const fileCount = this.state.fileCount;
    this.setState({
      uploadingImage: fileCount + count > 0,
      fileCount: fileCount + count,
    });
    this.onFocus();
  };

  onCancelPressed = e => {
    const data = {};
    data[this.props.name] = "";
    this.setState({ uploadObj: {} });
    this.onUpdateValue("");
    e.stopPropagation();
  };

  onAddFromUnsplash = () => {
    this.toggleResourceBank(true);
  };

  onAddFromPixabay = () => {
    this.toggleResourceBank(true);
  };

  toggleMediaModal = value => {
    this.setState({ showMediaModal: value });
  };

  croppedFile = async file => {
    // Use setState callback promise to ensure state is updated before proceeding
    await new Promise(resolve => {
      this.setState(
        {
          file: file.preview,
          croppedFile: undefined,
          name: file.name,
        },
        resolve
      );
    });

    this.props.showCroppingModal &&
      !this.props.isVideoSupported &&
      (await this.waitFor(() => this.state.croppedFile || !this.state.file));
    return this.props.showCroppingModal && !this.props.isVideoSupported
      ? this.state.croppedFile
      : file;
  };

  dataURLtoBlob = dataUrl => {
    var arr = dataUrl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new Blob([u8arr], { type: mime });
  };
  onAddContent = async dataImage => {
    const blob = this.dataURLtoBlob(dataImage);
    const type = blob.type;
    const preview = URL.createObjectURL(this.dataURLtoBlob(dataImage));
    const file = new File([blob], this.state.name, {
      type: type,
    });
    file.preview = preview;
    this.setState({ croppedFile: file, file: "" });
  };

  dropzoneRenderComponent = () => {
    const {
      progressOfUploads,
      value,
      showUnsplash,
      parentId,
      parentType,
      uploadingObjRedux,
      ratio,
      text,
      disabled = false,
      icon,
      isVideoSupported,
      t,
      customUploadComponent,
      customUploadButton,
      showCroppingModal,
      testId,
      editButtonType,
      dsVersion,
      uploadIcon,
    } = this.props;
    const {
      uploadObj: uploadObjState,
      uploadingImage,
      fileType,
      videoPreviewImage,
      showMediaModal,
    } = this.state;

    const uploadObj =
      (!!parentType && !!parentId) || !_.isEmpty(uploadingObjRedux)
        ? _.get(uploadingObjRedux, "attachment", {})
        : uploadObjState;
    const progressValue = parseInt(
      _.get(
        progressOfUploads,
        `${this.state.uploadObj.id}.progress`,
        uploadingImage ? "0" : "100"
      )
    );

    const isVideo = isVideoSupported && fileType === "VIDEO";

    const isPixabayFeatureFlagEnabled = ACLStore.can(
      "FeatureFlag:getImagesFromPixabay"
    );

    const isPexelsFeatureFlagEnabled = ACLStore.can(
      "FeatureFlag:getImagesFromPexels"
    );

    const attachment = [
      {
        type: "VIDEO",
        url: value,
      },
    ];
    const textForUploadComponent = text ? text : t("common:add_here");

    const onUploadFromDevice = () =>
      this.dropzoneRef.current.refs.wrappedInstance.dialogueBoxConfirm();

    const localUrl =
      this.state.croppedFile?.preview || this.state.file || uploadObj.url;

    const image =
      isVideo && value ? videoPreviewImage : getRethumb(localUrl, value);

    if (React.isValidElement(customUploadComponent)) {
      return React.cloneElement(customUploadComponent, {
        image,
        onUploadFromDevice,
        onAddFromPixabay: this.onAddFromPixabay,
        onDeleteClick: this.onDeleteClick,
        progressValue,
      });
    }

    return (
      <div style={{ width: "100%" }}>
        <Upload
          ratio={ratio}
          image={image}
          restrictCroppingToImage={false}
          progress={progressValue}
          onDeleteClick={this.onDeleteClick}
          isUnsplashEnabled={
            showUnsplash && ACLStore.can("TeacherPortal:SearchFromUnsplash")
          }
          isPixabayEnabled={
            showUnsplash &&
            (isPixabayFeatureFlagEnabled || isPexelsFeatureFlagEnabled)
          }
          onUploadFromDevice={onUploadFromDevice}
          onAddFromUnsplash={this.onAddFromUnsplash}
          onAddFromPixabay={this.onAddFromPixabay}
          text={textForUploadComponent}
          disabled={disabled}
          icon={uploadIcon === undefined ? icon : uploadIcon}
          type={isVideo ? "video" : "image"}
          onPlayClick={() => this.toggleMediaModal(true)}
          onAddImageClick={image => {
            this.onAddContent(image);
          }}
          isCropModalOpen={
            this.state.file && showCroppingModal && !this.props.isVideoSupported
          }
          onCloseCropModal={this.onCancel}
          testId={testId}
          editButtonType={editButtonType}
          dsVersion={dsVersion}
          customUploadButton={customUploadButton}
        ></Upload>
        {showMediaModal ? (
          <MediaModal
            attachments={attachment}
            toggleMediaModal={this.toggleMediaModal}
          />
        ) : null}
      </div>
    );
  };

  renderDropZone = () => {
    const {
      value,
      showUnsplash,
      parentId,
      parentType,
      uploadingObjRedux,
      accept,
      showAllImportOptions = false,
      disabled,
      testId,
      shouldReturnSignedUrl,
    } = this.props;
    const { uploadObj: uploadObjState } = this.state;

    const uploadObj =
      (!!parentType && !!parentId) || !_.isEmpty(uploadingObjRedux)
        ? _.get(uploadingObjRedux, "attachment", {})
        : uploadObjState;

    const url = uploadObj.url || value;
    const renderComponent = this.dropzoneRenderComponent();
    const disableClick =
      this.state.croppedFile?.preview ||
      this.state.file ||
      showAllImportOptions ||
      (!_.isEmpty(url) && showUnsplash) ||
      disabled;

    return (
      <DropzoneComponent
        ref={this.dropzoneRef}
        parentType={parentType}
        parentId={parentId}
        accept={accept}
        onDropContent={this.onDropContent}
        onContentUploaded={this.onContentUploaded}
        multiple={false}
        updateFilesCount={this.updateFilesCount}
        disableClick={disableClick}
        renderComponent={renderComponent}
        customizedFile={
          this.props.showCroppingModal && !this.props.isVideoSupported
            ? this.croppedFile
            : null
        }
        customizedFileObject={this.customizedFileObject}
        testId={`${testId}-imageDropzone`}
        shouldReturnSignedUrl={shouldReturnSignedUrl}
      />
    );
  };

  toggleResourceBank = (value = false) => {
    this.setState({ showResourceBank: value });
  };

  addResources = () => {};

  renderEdit = () => {
    const {
      uploadBoxStyles,
      cardSize,
      isDisabled,
      parentType,
      fieldParentType,
      parentId,
      dsVersion,
      fieldParentId,
    } = this.props;
    const { showResourceBank } = this.state;

    const component = this.renderDropZone();
    const uploadImageComponentClass = classNames(
      { [classes.uploadImageComponent]: true },
      { [classes.smallUploadImageComponent]: cardSize == "small" }
    );

    return (
      <div className={uploadImageComponentClass} style={uploadBoxStyles}>
        {component}
        {isDisabled && <div className={classes.disbaledImageContainer}></div>}
        {!!showResourceBank && (
          <ResourceBank
            id={"CREATE_DROPDOWN"}
            onDropContent={this.onDropContent}
            multi={false}
            onSingleContentUploaded={this.onSingleContentUploaded}
            updateFilesCount={this.updateFilesCount}
            closeResource={this.toggleResourceBank}
            onAddClick={this.addResources}
            parentType={parentType}
            fieldParentType={fieldParentType}
            fieldParentId={fieldParentId}
            parentId={parentId}
            dsVersion={dsVersion}
          />
        )}
      </div>
    );
  };
}

ImageDropZoneV2.propTypes = {
  ...UIBaseComponent.propTypes,
  onCancelPressed: PropTypes.func,
  onDropContent: PropTypes.func,
  testId: PropTypes.string,
  shouldReturnSignedUrl: PropTypes.bool,
  dsVersion: PropTypes.string,
};

ImageDropZoneV2.defaultProps = {
  ...UIBaseComponent.defaultProps,
  cardSize: "large",
  ratio: "16:9",
  showUnsplash: true,
  shouldUpload: true,
  buttonSize: "medium",
  onDropContent: _.noop,
  showCroppingModal: false,
  testId: "UIComponents",
  shouldReturnSignedUrl: false,
  dsVersion: "1.0",
};

const mapActionCreators = {
  uploadFileV2,
};

const mapStateToProps = (state, ownProps) => {
  const progressOfUploads = state.app_services.progressOfUploads;
  const isVideoSupported = _.get(ownProps, "isVideoSupported", false);
  const imageTypes = "image/jpeg,image/png,image/jpg,image/heic,image/heif";
  const videoTypes = "video/quicktime,video/mp4";
  const accept = isVideoSupported
    ? _.join([imageTypes, videoTypes], ",")
    : imageTypes;

  const icon = isVideoSupported ? (
    <VideosOutlined variant="subtle" />
  ) : (
    <ImageSquareOutlined />
  );

  return {
    progressOfUploads,
    uploadingObjRedux:
      _.find(
        Object.values(progressOfUploads),
        item =>
          item.parentType == ownProps.parentType &&
          item.parentId == ownProps.parentId
      ) || {},
    accept,
    icon,
  };
};

export default compose(
  I18nHOC({ resource: "common" }),
  connect(mapStateToProps, mapActionCreators)
)(ImageDropZoneV2);
